import * as React from 'react';
import { ClientId, LocationId, ShareableClient, t } from '@deltasierra/shared';
import { Input } from '..';

export type MultipleClientPickerProps = {
    clients: ShareableClient[];
    disabledId: ClientId;
    onSelectionChanged: (clientId: ClientId, value: boolean) => void;
    selectedClients: ClientId[];
};

export type MultipleClientPickerData = ShareableClient & { checked: boolean };

export const MultipleClientPicker: React.FunctionComponent<MultipleClientPickerProps> = ({
    clients,
    disabledId,
    onSelectionChanged,
    selectedClients,
}) => {
    const [searchTerm, setSearchTerm] = React.useState('');

    const selectChange = React.useCallback(
        (clientId: ClientId, value: boolean) => {
            onSelectionChanged(clientId, value);
        },
        [onSelectionChanged],
    );

    return (
        <div className="col-sm-10">
            <div style={{ float: 'left' }}>
                <div
                    className="well"
                    style={{
                        background: 'white',
                        height: 200,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        width: 200,
                    }}
                >
                    <Input
                        clearable
                        placeholder={`${t('COMMON.FILTER')}...`}
                        style={{ width: '100%' }}
                        onTermChange={setSearchTerm}
                    />
                    <MultipleClientPickerList
                        clients={clients}
                        disabledId={disabledId}
                        searchTerm={searchTerm}
                        selectedClients={selectedClients}
                        onSelectionChanged={selectChange}
                    />
                </div>
            </div>
            <div style={{ float: 'left', paddingLeft: 15, paddingTop: 8 }}>
                <SelectedModelsList
                    header={t('COMMON.SELECTED_CLIENTS')}
                    models={clients}
                    selectedModels={selectedClients}
                />
            </div>
        </div>
    );
};

type MultipleClientPickerListItemProps = {
    client: ShareableClient;
    checked: boolean;
    disabled: boolean;
    onChange: (clientId: ClientId, value: boolean) => void;
};

const MultipleClientPickerListItem: React.FunctionComponent<MultipleClientPickerListItemProps> = ({
    checked,
    client,
    disabled,
    onChange,
}) => (
    <div className="checkbox">
        <label>
            <input
                checked={checked}
                disabled={disabled}
                type="checkbox"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(client.clientId, event.target.checked)
                }
            />
            {client.clientTitle}
            {client.numCollections >= 20 ? ' *' : ''}
        </label>
    </div>
);

export type MultipleClientPickerListProps = {
    clients: ShareableClient[];
    disabledId: ClientId;
    searchTerm: string;
    selectedClients: Array<ClientId | LocationId>;
    onSelectionChanged: (clientId: ClientId, value: boolean) => void;
};

const MultipleClientPickerList: React.FunctionComponent<MultipleClientPickerListProps> = ({
    clients,
    disabledId,
    onSelectionChanged,
    searchTerm,
    selectedClients,
}) => (
    <>
        {clients
            .filter(client => client.clientTitle.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
            .map(client => {
                const checked = selectedClients.findIndex(id => client.clientId === id) > -1;
                const disabled = client.clientId === disabledId || (client.numCollections >= 20 && !checked);
                return (
                    <MultipleClientPickerListItem
                        checked={checked}
                        client={client}
                        disabled={disabled}
                        key={client.clientId}
                        onChange={onSelectionChanged}
                    />
                );
            })}
    </>
);

export type SelectedModelsListProps = {
    models: ShareableClient[];
    header: string;
    selectedModels: Array<ClientId | LocationId>;
};

export const SelectedModelsList: React.FunctionComponent<SelectedModelsListProps> = ({
    header,
    models,
    selectedModels,
}) => {
    const selected = models.reduce((val, client) => (selectedModels.includes(client.clientId) ? val + 1 : val), 0);

    return (
        <>
            <div className="branded-alternate">
                <h3 style={{ fontSize: 18 }}>{`${header} (${selected})`}</h3>
            </div>
            <ul style={{ listStyleType: 'disc' }}>
                {models.map(
                    client =>
                        selectedModels.includes(client.clientId) && <li key={client.clientId}>{client.clientTitle}</li>,
                )}
            </ul>
        </>
    );
};
